import {
    Link,
    NextImage,
    Popover,
    PopoverContent,
    PopoverTrigger,
    cn,
    transMoney,
    useI18n,
    useProduct,
    useSuggestions,
} from "@jog/react-components"
import { TweakwiseItem } from "@jog/types"

import { Input } from "../InputText/Input"

const SearchItem = ({ item }: { item: TweakwiseItem }) => {
    const { isDiscount, price, firstPrice, linkItem, image, name } = useProduct(item)
    const { $t } = useI18n()
    const _name = name.replace(/^./, (v) => v.toUpperCase())
    return (
        <div className="relative flex items-center px-3 py-2 text-sm hover:bg-gray-bg">
            <div className="shrink-0 p-2">
                <NextImage blur={false} height={80} width={54} src={image} alt={_name} />
            </div>
            <div className="h-25 flex-1 p-2">
                <div>{_name}</div>
                <div className="mt-1.5">
                    <div
                        className={cn(
                            isDiscount ? "text-red-main" : "text-black",
                            "mt-1 font-primary text-13 font-medium leading-4"
                        )}>
                        {transMoney(price * 100, false)}
                    </div>
                    {isDiscount ? (
                        <div className="mt-1 text-10 text-grey-dark">
                            {$t(`old price`)}: {transMoney(+firstPrice * 100, false)}
                        </div>
                    ) : null}
                </div>
            </div>
            <Link href={linkItem} className="absolute inset-0" aria-label={_name} />
        </div>
    )
}

const SearchIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <g clipPath="url(#clip0_155_254)">
            <path
                d="M22.6464 23.3536C22.8417 23.5488 23.1583 23.5488 23.3536 23.3536C23.5488 23.1583 23.5488 22.8417 23.3536 22.6465L22.6464 23.3536ZM18.6176 10.0588C18.6176 14.7857 14.7857 18.6176 10.0588 18.6176V19.6176C15.338 19.6176 19.6176 15.338 19.6176 10.0588H18.6176ZM10.0588 18.6176C5.33192 18.6176 1.5 14.7857 1.5 10.0588H0.5C0.5 15.338 4.77963 19.6176 10.0588 19.6176V18.6176ZM1.5 10.0588C1.5 5.33192 5.33192 1.5 10.0588 1.5V0.5C4.77963 0.5 0.5 4.77963 0.5 10.0588H1.5ZM10.0588 1.5C14.7857 1.5 18.6176 5.33192 18.6176 10.0588H19.6176C19.6176 4.77963 15.338 0.5 10.0588 0.5V1.5ZM16.1759 16.883L22.6464 23.3536L23.3536 22.6465L16.883 16.1759L16.1759 16.883Z"
                fill="currentColor"
            />
        </g>
        <defs>
            <clipPath>
                <rect width="24" height="24" fill="white" />
            </clipPath>
        </defs>
    </svg>
)

export const Suggestion = () => {
    const { $t } = useI18n()
    const { handleSearch, suggestions, isSearchShown, searchTerm, setSearchTerm, setIsSearchShown } = useSuggestions()

    return (
        <div className="flex h-full w-full items-center justify-end">
            <form
                className="relative w-full"
                onSubmit={async (e) => {
                    e.preventDefault()
                    await handleSearch(searchTerm)
                }}>
                <button
                    type="submit"
                    aria-label={$t("Search")}
                    className="absolute top-2 right-2 flex h-6 w-6 cursor-pointer items-center justify-center pl-1 lg:top-1 lg:right-0">
                    <SearchIcon />
                </button>
                <Popover open={isSearchShown} onOpenChange={setIsSearchShown}>
                    <PopoverTrigger className="flex w-full">
                        <Input
                            name="search"
                            type="text"
                            placeholder={$t("Search")}
                            className="w-full pr-6 text-left text-xs uppercase lg:border-transparent lg:!border-b-black lg:pl-0 lg:!text-left lg:focus:border-transparent"
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </PopoverTrigger>
                    <PopoverContent
                        onOpenAutoFocus={(e) => e.preventDefault()}
                        align="end"
                        className="-mt-1.25 max-h-[80vh] w-screen overflow-y-auto rounded-none p-0 lg:mt-1.25 lg:w-[400px]">
                        <div>
                            {suggestions?.searchPhrases?.length ? (
                                <div>
                                    {suggestions.searchPhrases.map((i) => (
                                        <div key={i.name}>
                                            {i.name ? (
                                                <div className="bg-grey-bg p-3 text-sm font-medium">{i.name}</div>
                                            ) : null}
                                            <div>
                                                {i.suggestions.slice(0, 5).map((item, i) => (
                                                    <Link
                                                        onClick={() => setIsSearchShown(false)}
                                                        href={`/search?tn_q=${item.match}`}
                                                        className="block w-full p-3 text-sm hover:bg-gray-bg hover:text-orange-main"
                                                        key={i}>
                                                        {item.match}
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <></>
                            )}
                            {suggestions?.categories?.length ? (
                                <div>
                                    {suggestions.categories.map((i) => (
                                        <div key={i.name}>
                                            {i.name ? (
                                                <div className="bg-grey-bg p-3 text-sm font-medium">{i.name}</div>
                                            ) : null}
                                            <div>
                                                {i.suggestions.slice(0, 5).map((item, i) => (
                                                    <Link
                                                        onClick={() => setIsSearchShown(false)}
                                                        href={item.navigationLink.context.category.link}
                                                        className="block w-full p-3 text-sm hover:bg-gray-bg hover:text-orange-main"
                                                        key={i}>
                                                        {item.navigationLink.context.category.parentName ? (
                                                            <span className="mr-1">
                                                                {`${item.navigationLink.context.category.parentName} > `}
                                                            </span>
                                                        ) : null}
                                                        <span>{item.match}</span>
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <></>
                            )}
                            {suggestions?.products?.items?.length ? (
                                <div>
                                    {suggestions.products.name ? (
                                        <div className="bg-grey-bg p-3 text-sm font-medium">
                                            {suggestions.products.name}
                                        </div>
                                    ) : null}
                                    <div>
                                        {suggestions.products.items.slice(0, 3).map((item) => (
                                            <SearchItem item={item} key={item.itemno} />
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <></>
                            )}
                        </div>
                    </PopoverContent>
                </Popover>
            </form>
        </div>
    )
}
